import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { PerkVoucherEmail } from 'src/app/interfaces/perk';
import { Pet, PetSpeciesFilterableType, PetStatus } from 'src/app/interfaces/pet';
import {
  ProductType,
  User,
  UserAddress,
  UserContact,
  BannerDetails,
  RegistrationPaymentInfo,
  RegistrationStepsResponse,
} from '../../interfaces/user';
import { Invoice } from 'src/app/interfaces/invoice';
import { UpdateOwnerDetailDTO } from 'src/app/interfaces/Account/updateOwnerDetailDTO';
import { CardDetail } from 'src/app/interfaces/Account/cardDetail';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  user: User = null;
  options = {
    disableUnauthRedirect: false,
    lastUrlRequested: null,
  };
  isBrowser: boolean;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  getPetsForUserOrderedById(user: User) {
    return user.pets.sort((a, b) => {
      if (a.id > b.id) {
        return 1;
      }
      if (a.id < b.id) {
        return -1;
      }
      return 0;
    });
  }

  sendVoucherEmail(shortCode: string): void {
    this.http
      .get<PerkVoucherEmail>(
        `${environment.apiUrl}/api/v2/perks/${shortCode}/user/${this.user.id}?scheduleEmail=true`
      )
      .subscribe(
        () => {},
        (err) => {
          console.log(err);
        }
      )
      .add(() => {});
  }

  getMe(skipInterceptor?): Observable<User> {
    let headers = new HttpHeaders();
    if (skipInterceptor) {
      headers = headers.append('Skip-Interceptors', ['unauth']);
    }
    return this.http.get<User>(`${environment.apiUrl}/api/users/me`, { headers }).pipe(
      map((user) => {
        user.created = new Date(`${user.created}Z`);
        user.addresses.forEach((address) => {
          if (address.temporary && address.temporary.dateStart && address.temporary.dateEnd) {
            if (address.temporary.dateStart) {
              address.temporary.dateStart = new Date(`${address.temporary.dateStart}Z`);
            }
            if (address.temporary.dateEnd) {
              address.temporary.dateEnd = new Date(`${address.temporary.dateEnd}Z`);
            }
          } else {
            address.temporary = null;
          }
        });
        user.pets.forEach((pet) => {
          if (pet.dob) {
            pet.dob = new Date(`${pet.dob}Z`);
          }
          if (pet.implantDate) {
            pet.implantDate = new Date(`${pet.implantDate}Z`);
          }
          pet.missingPetReports.forEach((report) => {
            report.lastSeen.date = new Date(`${report.lastSeen.date}Z`);
          });
        });
        // Sorting the pets array so that the ones the needs registration to be completed will be always the firsts to be displayed
        user.pets.sort((a, b) => {
          if (
            a.name !== '[to_be_completed_by_keeper]' &&
            b.name === '[to_be_completed_by_keeper]'
          ) {
            return 1;
          }
          if (
            a.name === '[to_be_completed_by_keeper]' &&
            b.name !== '[to_be_completed_by_keeper]'
          ) {
            return -1;
          }
          return 0;
        });
        this.user = JSON.parse(JSON.stringify(user));
        return user;
      })
    );
  }

  saveMe(user: User): Observable<User> {
    return this.http.put<User>(`${environment.apiUrl}/api/users/me`, user);
  }

  patchMe(user: Partial<User>): Observable<User> {
    return this.http.patch<User>(`${environment.apiUrl}/api/users/me`, user);
  }

  saveTelephoneNumbers(mainPhoneNumber: string, secondaryPhoneNumber: string) {
    const partialUserUpdate: Partial<User> = {
      contact: {
        mainPhoneNumber: mainPhoneNumber,
        secondaryPhoneNumber: secondaryPhoneNumber
      },
    };
    return this.patchMe(partialUserUpdate);
  }

  saveDatOfBirth(dateOfBirth: Date) {
    const partialUserUpdate: Partial<User> = {
      dateOfBirth: dateOfBirth,
    };
    return this.patchMe(partialUserUpdate);
  }

  saveGender(gender: string) {
    const partialUserUpdate: Partial<User> = {
      gender: gender,
    };
    return this.patchMe(partialUserUpdate);
  }

  saveTitle(title: string) {
    const partialUserUpdate: Partial<User> = {
      title: title,
    };
    return this.patchMe(partialUserUpdate);
  }

  updateOwnerAddresses(ownerAddress: UserAddress[], userContact: UserContact) {
    const partialUserUpdate: Partial<User> = {
      addresses: ownerAddress,
      contact: userContact,
    };
    return this.patchMe(partialUserUpdate);
  }

  updateOwnerDetailsAndAddresses(ownerDetail: UpdateOwnerDetailDTO, ownerAddress: UserAddress[]) {
    const partialUserUpdate: Partial<User> = {
      name: {
        firstName: ownerDetail.firstName,
        lastName: ownerDetail.lastName,
      },
      contact: {
        mainPhoneNumber: ownerDetail.mainPhoneNumber,
        secondaryPhoneNumber: ownerDetail.secondaryPhoneNumber,
      },
      title: ownerDetail.title,
      dateOfBirth: ownerDetail.dateOfBirth,
      gender: ownerDetail.gender,
      addresses: ownerAddress,
    };

    return this.patchMe(partialUserUpdate);
  }

  updatePassword(passwords: any): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}/api/identity/password`, passwords);
  }

  updateEmail(changeEmail: any): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}/api/users/me/changeemail`, changeEmail);
  }

  saveAddress(address: UserAddress): Observable<UserAddress> {
    const payload: UserAddress = JSON.parse(JSON.stringify(address));

    if (!payload.temporary && !payload.temporaryPhoneNumber?.trim()) {
      payload.temporaryPhoneNumber = null;
    }

    let addressObservable: Observable<UserAddress>;

    if (payload.id) {
      addressObservable = this.http.put<UserAddress>(
        `${environment.apiUrl}/api/users/me/addresses/${payload.id}`,
        payload
      );
    } else {
      addressObservable = this.http.post<UserAddress>(
        `${environment.apiUrl}/api/users/me/addresses`,
        payload
      );
    }

    return addressObservable.pipe(
      catchError((err) => {
        if (err instanceof HttpErrorResponse && err.status === 400) {
          if (err.error && err.error.error_code === 'invalid_postcode') {
            return throwError(
              'Cannot save your address. Please ensure that you have provided a full valid UK postcode.'
            );
          }
          return throwError(
            'Cannot save your address. Please make sure all required fields are valid.'
          );
        }
        return throwError('An error has occurred. Please try again later.');
      })
    );
  }

  areDetailsProvidedFor(user: User): boolean {
    const hasPhone = !!user.contact.mainPhoneNumber?.trim() || !!user.contact.secondaryPhoneNumber?.trim();
    return !!user.addresses.length && hasPhone;
  }

  petTypeCount(pets: Pet[], labelsAsType?: boolean): any {
    const count = {
      others: 0,
      dogs: 0,
      cats: 0,
      horses: 0,
      rabbits: 0,
    };
    pets.forEach((pet) => {
      if (pet.speciesFilter === PetSpeciesFilterableType.Other) {
        count.others++;
      } else if (pet.speciesFilter === PetSpeciesFilterableType.Dog) {
        count.dogs++;
      } else if (pet.speciesFilter === PetSpeciesFilterableType.Cat) {
        count.cats++;
      } else if (pet.speciesFilter === PetSpeciesFilterableType.Horse) {
        count.horses++;
      } else if (pet.speciesFilter === PetSpeciesFilterableType.Rabbit) {
        count.rabbits++;
      }
    });

    return labelsAsType
      ? {
          [PetSpeciesFilterableType.Other]: count.others,
          [PetSpeciesFilterableType.Dog]: count.dogs,
          [PetSpeciesFilterableType.Cat]: count.cats,
          [PetSpeciesFilterableType.Horse]: count.horses,
          [PetSpeciesFilterableType.Rabbit]: count.rabbits,
        }
      : count;
  }

  filterAliveRegisteredPets(pets: Pet[]): Pet[] {
    return pets.filter(
      (pet) => pet.status === PetStatus.Alive && pet.name !== '[to_be_completed_by_keeper]'
    );
  }

  filterAlivePets(pets: Pet[]): Pet[] {
    return pets.filter((pet) => pet.status === PetStatus.Alive);
  }

  filterTokPets(pets: Pet[]): Pet[] {
    return pets.filter((pet) => pet.isTokPaymentRequired);
  }

  filterIncompletePetRegistrations(pets: Pet[]): Pet[] {
    return pets.filter((pet) => pet.name === '[to_be_completed_by_keeper]');
  }

  identify(user: User): void {
    const aliveRegisteredPets = this.filterAliveRegisteredPets(user.pets);
    const incompletePetRegistrations = this.filterIncompletePetRegistrations(user.pets);
    const now = new Date();

    if (this.isBrowser) {
      window.analytics.identify(user.id, {
        userId: user.id,
        email: user.contact.email,
        telephone: user.contact.mainPhoneNumber || null,
        productType: user.productType,
        petType: this.petTypeCount(aliveRegisteredPets),
        emailMarketingConsent: user.communicationPreferences.email,
        smsMarketingConsent: user.communicationPreferences.sms,
        alivePetNames: aliveRegisteredPets.map((pet) => pet.name).join(', '),
        numberOfAlivePets: aliveRegisteredPets.length,
        lastActiveDateTimeFromJSClient: now.toISOString(),
        numberOfIncompletePetRegistrations: incompletePetRegistrations.length,
      });
    }
  }

  sendEnquiry(enquiry: any): Observable<any> {
    return this.http.post(`${environment.apiUrl}/api/contact-us`, enquiry);
  }

  getInvoices(): Observable<Invoice[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/api/users/me/invoices`).pipe(
      map((invoices) => {
        invoices.forEach((invoice) => {
          invoice.created = new Date(invoice.created);
          invoice.updated = new Date(invoice.updated);
        });
        return invoices as Invoice[];
      })
    );
  }

  public isUserBasicOrBasicPlus(productTypeId: number) {
    return this.isUserBasic(productTypeId) || productTypeId === ProductType.BasicPlus;
  }

  public isUserBasic(productTypeId: number) {
    return productTypeId === ProductType.Basic;
  }

  public isUserLocator(productTypeId: number) {
    return productTypeId === ProductType.Locator;
  }

  public isUserAlertOrFind(productTypeId: number) {
    return productTypeId === ProductType.Premium;
  }

  public isUserPremium(productTypeId: number) {
    return (
      productTypeId === ProductType.LegacyPlus ||
      productTypeId === ProductType.LegacyPremium ||
      productTypeId === ProductType.Premium
    );
  }

  public isUserStoredInServicePremium() {
    return (
      this.user?.productType === ProductType.LegacyPlus ||
      this.user?.productType === ProductType.LegacyPremium ||
      this.user?.productType === ProductType.Premium
    );
  }

  public isUserStoredInServiceBasicPlus() {
    return this.user?.productType === ProductType.BasicPlus;
  }

  public isTOKFreeForUser(user: User) {
    return !this.isUserBasicOrBasicPlus(user.productType);
  }

  public isOlderThan14Days(userCreated: Date) {
    const oneDay = 1000 * 60 * 60 * 24;

    return Math.floor((Date.now() - new Date(userCreated).getTime()) / oneDay) <= 14;
  }

  public doesEmailExist(email: string) {
    return this.http.get<any>(`${environment.apiUrl}/api/users/exists/${email}`);
  }

  public checkEmailOrPhoneNumber(identifier: string, identifierType: number) {
    const base64Encoded = btoa(identifier);
    return this.http.get<any>(
      `${environment.apiUrl}/api/v1/users/${base64Encoded}?identifierType=${identifierType}`
    );
  }

  public getUserCards() {
    return this.http.get<CardDetail[]>(`${environment.apiUrl}/api/stripe/paymentMethod`);
  }

  public getUserCardUpdateLink() {
    return this.http.post<string>(`${environment.apiUrl}/api/stripe/paymentMethod/update`, {});
  }

  public getBannersDetails() {
    return this.http.get<BannerDetails[]>(`${environment.apiUrl}/api/banners`);
  }

  public getRegistrationPaymentConfig(): Observable<RegistrationPaymentInfo> {
    return this.http.get<RegistrationPaymentInfo>(`${environment.apiUrl}/api/registration/me/payment`);
  }

  public getRegistrationSteps(): Observable<RegistrationStepsResponse> {
    return this.http.get<RegistrationStepsResponse>(
      `${environment.apiUrl}/api/registration/me/steps`
    );
  }

  // Marks a registration step as completed.
  public completeRegistrationStep(step: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/api/registration/me/steps`, { step });
  }
}
